import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, OnDestroy, Signal, signal, untracked } from '@angular/core';
import { AppContext } from 'src/app/app/context';
import { ChunkResult, RowsResult, VisualDataQuery, VisualDataQueryColumnCollection, VisualDataResource, VisualDataSource } from 'src/app/pbir/visuals/data';
import { AdvancedFilterLogicalOperator, ErrorDetails, FilterDto } from 'src/app/services/api.services';
import { DataFormatter, DisplayFormatArgs } from '../../formatting/DataFormatter';
import { Predicate, PredicateType } from '../../utils/Predicate';
import { VisualDataAdapter } from '../data/VisualDataAdapter';
import { VisualDataRequest } from '../data/VisualDataRequest';
import { Visual } from '../Visual';
import { VisualContentError } from '../VisualContentError';
import { VisualCustomField } from '../VisualCustomField';
import { VisualField } from '../VisualField';
import { VisualFilter } from '../VisualFilter';
import { VISUAL_HOST, VisualHost, VisualRowCountState } from '../VisualHost';
import { CrossFilterGroup, DataPoint } from '../VisualInteractions';
import { VisualStyle } from '../VisualStyle';

@Component({
  selector: 'pbir-visual',
  template: ``,
  standalone: false,
  host: {
    '[style]': 'styleCss()',
    '(mousedown)': 'onClick($event)'
  }
})
export abstract class VisualComponent<
  TOptions extends Visual.Options = Visual.Options,
  TState extends Visual.State = Visual.State,
  TStyle extends VisualStyle = VisualStyle,
  TData extends VisualComponent.Data = VisualComponent.Data> implements OnDestroy {
  readonly destroyRef = inject(DestroyRef);
  readonly appContext = inject(AppContext);
  readonly host = inject<VisualHost>(VISUAL_HOST);

  get disableAnimations() { return true || this.host.disableAnimations; }


  broadcastEvent<T extends VisualHost.EventBase>(e: T): void {
    this.host.broadcastEvent(e);
  }

  onEvent<T extends VisualHost.Event>(e: T): void {
    switch (e.type) {
      case VisualHost.EventType.Loaded:
        this.onLoaded(<VisualHost.LoadedEvent><unknown>e);
        break;

      case VisualHost.EventType.BeforeSave:
        this.onBeforeSave(<VisualHost.BeforeSaveEvent><unknown>e);
        break;

      case VisualHost.EventType.Saved:
        this.onSaved(<VisualHost.SavedEvent><unknown>e);
        break;

      case VisualHost.EventType.FieldRenamedEvent:
        this.onFieldRenamed(<VisualHost.FieldRenamedEvent><unknown>e);
        break;


      case VisualHost.EventType.SelectionChangeEvent:
        this.onSelectionChange(<VisualHost.SelectionChangeEvent><unknown>e);
        break;

      case VisualHost.EventType.SelectionClearedEvent:
        this.onSelectionCleared(<VisualHost.SelectionClearedEvent><unknown>e);
        break;

      case VisualHost.EventType.NonDataPointSelectionChangeEvent:
        this.onNonDataPointSelectionChange(<VisualHost.NonDataPointSelectionChangeEvent><unknown>e);
        break;

      case VisualHost.EventType.CrossFilterBecomeSource:
        this.onBecomeCrossFilterSource(<VisualHost.CrossFilterBecomeSourceEvent><unknown>e);
        break;

      case VisualHost.EventType.CrossFilterBecomeTarget:
        this.onBecomeCrossFilterTarget(<VisualHost.CrossFilterBecomeTargetEvent><unknown>e);
        break;
    }
  }

  protected onFieldRenamed(e: VisualHost.FieldRenamedEvent): void { };

  protected onBeforeSave(e: VisualHost.BeforeSaveEvent): void { }

  protected onSaved(e: VisualHost.SavedEvent): void { }

  protected onLoaded(e: VisualHost.LoadedEvent): void {
    if (this.context.inEditor) {
      this.selectionClear();
    }
  }


  get dataPoints() { return this.host.dataPoints; }
  get dataPointSelection() { return this.host.dataPointSelection; }
  get nonDataPointSelectionActive() { return this.host.nonDataPointSelectionActive; }
  get hasSelection() { return this.host.hasSelection; }

  protected onSelectionChange(e: VisualHost.SelectionChangeEvent): void {
    untracked(() => {
      if (this.dataPointSelection.size()) {
      }
    });
  }

  protected onSelectionCleared(e: VisualHost.SelectionClearedEvent): void {
  }

  protected onNonDataPointSelectionChange(e: VisualHost.NonDataPointSelectionChangeEvent): void {
  }

  protected selectionSet(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger = VisualHost.EventTrigger.Visual) {
    this.host.selectionSet(dataPoints, trigger);
  }

  protected selectionAdd(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger = VisualHost.EventTrigger.Visual) {
    this.host.selectionAdd(dataPoints, trigger);
  }

  protected selectionRemove(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger = VisualHost.EventTrigger.Visual) {
    this.host.selectionRemove(dataPoints, trigger);
  }

  protected selectionClear(trigger: VisualHost.EventTrigger = VisualHost.EventTrigger.Visual) {
    this.host.selectionClear(trigger);
  }

  protected setNonDataPointSelectionActive(value: boolean, trigger: VisualHost.EventTrigger = VisualHost.EventTrigger.Visual) {
    this.host.setNonDataPointSelectionActive(value, trigger);
  }


  readonly busy = input<boolean>(false);
  readonly visual = input.required<Visual<TOptions, TState, TStyle>>();

  get context() { return this.visual()?.context; }
  get interactions() { return this.context?.interactions; }

  get dataModel() { return this.context?.dataModel; }
  get schema() { return this.context?.schema; }
  get customFields() { return this.context?.customFields; }

  get type() { return this.visual().type; }
  get options() { return this.visual().options; }
  get state() { return this.visual().state; }
  get style() { return this.visual().style; }

  get fields() { return this.visual().fields; }
  get filters() { return this.visual().filters; }

  get locale() { return this.visual().locale; }
  get tz() { return this.visual().tz; }
  get currency() { return this.visual().currency; }

  /** Returns legacy PBI chart type, when one is applicable. */
  readonly pbiChartType = computed(() => {
    let v = this.visual();
    if (!v)
      return null;

    return v.type.getPbiChartType(v);
  });

  /** 
   * Whether this component's visual is valid and should be rendered.
   * When this is false, data query will not be available. */
  readonly isVisualValid = computed(() => !!this.context && !!this.visual()?.isValid());

  readonly exceededRowLimits = signal(false);
  readonly exceededRenderRowLimits = signal(false);

  readonly styleCss = computed(() => this.style().toCss());

  readonly request = computed<VisualDataRequest>(() => {
    if (!this.isVisualValid())
      return null;

    return this.computeRequest();
  });

  protected computeRequest(): VisualDataRequest {
    return new VisualDataRequest(this.dataModel, {
      debugName: `${this.context.debugName()}.data`,
      schema: this.schema,
      fields: this.requestFields(),
      filterGroups: this.requestFilterGroups(),
      customFields: this.requestCustomFields(),
      currency: this.currency(),
      dontApplyFieldSorting: !this.type.includeSortingInQuery,
      visualId: this.context.visualData()?.visualId,
      recordPlan: !!this.appContext.features()?.dataQuery?.recordPlansForFrontendQueries
    });
  }

  readonly requestFields = computed(() => this.computeRequestFields());

  protected computeRequestFields(): ReadonlyArray<VisualField> {
    let fields = [...this.fields.all({includeInvalidCalculatedField: false})];

    if (!fields.some(f => f.sorted()))
      fields = this.applyDefaultSorting(fields);

    return fields;
  }

  protected applyDefaultSorting(fields: VisualField[]): VisualField[] {
    return fields;
  }


  readonly requestFilterGroups = computed(
    () => this.computeRequestFilterGroups(),
    { equal: VisualFilter.PredicateGroup.arrayEqual });

  protected computeRequestFilterGroups(): ReadonlyArray<VisualFilter.PredicateGroup> {
    let groups: VisualFilter.PredicateGroup[] = [];

    let filters = [...(this.interactions?.requestFilters.bySchema.get(this.schema)() ?? []), ...this.filters()]
      .filter(x => x && !x.disabled() && x.isValid() && x.isComplete())
      .map(x => Predicate.normalize(x.predicate()));

    let aggregatedFilters = filters.filter(x => x.target.isAggregated);
    let unaggregatedFilters = filters.filter(x => !x.target.isAggregated);

    for (let g of this.appliedCrossFilterGroups()) {
      let crossFilters = g
        .filters
        .map(x => Predicate.normalize(x.predicate()));
        
      groups.push(({
        operator: g.operator,
        filters: crossFilters
      }));
    }
    
    groups.push(({
      operator: AdvancedFilterLogicalOperator.And,
      filters: unaggregatedFilters
    }));

    groups.push(({
      operator: AdvancedFilterLogicalOperator.And,
      filters: aggregatedFilters
    }));

    return groups;
  }


  readonly requestCustomFields = computed(() => this.computeRequestCustomFields());

  protected computeRequestCustomFields(): ReadonlyArray<VisualCustomField> {
    return [...this.customFields()];
  }


  get dataFormatter() { return this.context?.dataFormatter; }

  private readonly defaultFormatter = computed(() => this.createDefaultFormatter());

  protected createDefaultFormatter() {
    return new VisualComponent.Formatter(
      this,
      this.context.dataFormatter(),
      this.visual());
  }

  private formatterCache = new Map<any, any>();
  private lastFormatterResult: any;
  private lastFormatter: any;

  formatter(): VisualComponent.Formatter;
  formatter(result: RowsResult): VisualComponent.RowFormatter;
  formatter(result?: RowsResult): VisualComponent.Formatter | VisualComponent.RowFormatter {
    if (result) {
      if (result === this.lastFormatterResult) {
        return this.lastFormatter;
      }

      let formatter = this.formatterCache.get(result);
      if (!formatter) {
        formatter = this.createRowFormatter(result);
        this.formatterCache.set(result, formatter);
      }

      this.lastFormatterResult = result;
      this.lastFormatter = formatter;

      return formatter;
    }
    else {
      return this.defaultFormatter();
    }
  }

  protected createRowFormatter(result: RowsResult) {
    return new VisualComponent.RowFormatter(
      this,
      this.context.dataFormatter(),
      this.visual(),
      result.columns);
  }


  /** Called before value is consumed by formatter. **/
  beforeFormatValue(value: any) {
    return value;
  }

  beforeFormatValueForDisplay(value: any) {
    return this.beforeFormatValue(value);
  }

  beforeFormatValueForData(value: any) {
    return this.beforeFormatValue(value);
  }


  /** Called before value is consumed by formatter. **/
  afterFormatValue(value: any) {
    return value;
  }

  afterFormatValueForDisplay(value: any) {
    return this.afterFormatValue(value);
  }

  afterFormatValueForData(value: any) {
    return this.afterFormatValue(value);
  }


  readonly dataSource = input.required<VisualDataSource>();
  readonly data: TData = this.initData();

  /**
   * Initializes the data.
   * The default implementation uses the @see VisualComponent.Data class.
   **/
  initData(): TData | null {
    let data = new VisualComponent.DefaultData(this.request, this.dataSource);

    return <any>data;
  }

  /**
   * Returns QueryId for the primary query that populates this visual.
   **/
  getDataQueryId(): any {
    if (this.data instanceof VisualComponent.DefaultData)
      return this.data.query()?.id();

    return null;
  }


  readonly showRowCount = computed(() => !!this.type?.showRowCount && !!this.options()?.showRowCount);
  readonly rowCount = computed(() => this.data.rowCount());

  readonly rowCountChange = effect(() => {
    let rc = this.rowCount();
    if (!rc)
      return;

    if (rc.loaded) {
      if (rc.error) {
        this.broadcastEvent({
          type: VisualHost.EventType.RowCountErrorEvent,
          error: rc.error
        });
      }
      else {
        this.broadcastEvent({
          type: VisualHost.EventType.RowCountLoadedEvent,
          rowCount: rc.rowCount,
          exceededRowLimit: rc.exceededRowLimit
        });
      }
    }
    else {
      this.broadcastEvent({
        type: VisualHost.EventType.RowCountLoadingEvent,
      });
    }
  });

  readonly contentErrorChange = effect(() => {
    let contentError = this.data?.contentError();

    this.broadcastEvent({
      type: VisualHost.EventType.ContentErrorChangeEvent,
      error: contentError
    });

    if (contentError) {
      this.broadcastEvent({
        type: VisualHost.EventType.RenderComplete,

        failed: true,
        error: contentError
      });
    }
  });

  getNextSortOrder() {
    return (this.visual().fieldSorting()?.maxFieldSortOrder() ?? 0) + 1;
  }

  getIsSorting() {
    return (this.visual().fieldSorting()?.sortedFieldCount() ?? 0) > 0;
  }

  getIsMultiSorting() {
    return (this.visual().fieldSorting()?.sortedFieldCount() ?? 0) > 1;
  }



  private readonly loadedBroadcastOnce = signal(false);

  protected broadcastLoaded(allowMultiple?: boolean) {
    untracked(() => {
      if (!allowMultiple && this.loadedBroadcastOnce())
        return;

      this.loadedBroadcastOnce.set(true);
      this.broadcastEvent({ type: VisualHost.EventType.Loaded });
    });
  }

  private readonly broadcastLoadedOnDataLoaded = effect(() => {
    if (this.data.loaded()) {
      this.broadcastLoaded(this.context.inEditor);
    }
  });

  ngOnDestroy() {
    this.data.onDestroy();
  }

  broadcastStateChange() {
    this.broadcastEvent({
      type: VisualHost.EventType.StateChangeEvent
    });
  }


  readonly appliedCrossFilterGroups = computed(() => {
    let interactions = this.interactions;
    if (!interactions || this.host === interactions.crossFilter.source())
      return [];

    let sourceGroups = interactions.crossFilter.groups();

    return this.computeCrossFilterGroups(sourceGroups);
  });

  protected computeCrossFilterGroups(crossFilterGroups: ReadonlyArray<CrossFilterGroup>): ReadonlyArray<VisualFilter.FilterGroup> {
    let m = this.dataModel;
    let schemaDto = this.schema.getSchemaTargetDto();

    return crossFilterGroups.map(g => {
      let mapped = g.filters.flatMap(x => {
        let targets =
          m.relationshipMapping.getMapping(
            this.context.visualFieldTargetResolver(),
            x.field.target,
            {
              includeSelf: x.field.target.schema == this.schema,
              targetSchema: schemaDto
            });

        return targets.map(t => ({
          target: t,
          values: [x.value]
        }));
      });

      return mapped.map(x => new VisualFilter({
        predicate: {
          type: PredicateType.Basic,
          target: x.target,
          values: x.values,
          operator: Predicate.BasicOperator.OneOf
        }
      })).filter(x => x && !x.disabled() && x.isValid() && x.isComplete())
    }).map(x => ({
      operator: AdvancedFilterLogicalOperator.Or,
      filters: x
    }));
  }
  
  protected onBecomeCrossFilterSource(e: VisualHost.CrossFilterBecomeSourceEvent) {
    this.host.crossFilterGroups.set(this.computeSourceCrossFilterGroups());
  }

  protected onBecomeCrossFilterTarget(e: VisualHost.CrossFilterBecomeTargetEvent) {
    this.host.crossFilterGroups.set([]);
    this.host.selectionClear(e.trigger);
  }

  protected computeSourceCrossFilterGroups(): ReadonlyArray<CrossFilterGroup> {
    let dataPoints = [...this.dataPointSelection.values()];
    let unaggregatedGroupings = dataPoints.map(x => x.visitSelfAndParents(p => p.field && !p.field?.isAggregated).reverse());
    let crossFilterGroups = unaggregatedGroupings.map(x => new CrossFilterGroup(this.schema, x));
    return crossFilterGroups;
  }


  public captureState(state: Visual.State): boolean {
    let [locale, tz, currency] = [this.locale(), this.tz(), this.currency()];

    state.locale.set(locale);
    state.tz.set(tz);
    state.currency.set(currency);
    return true;
  }


  onClick(e: MouseEvent) {
    this.broadcastEvent({
      type: VisualHost.EventType.ClickEarlyEvent,
      mouseEvent: e,
      ...this.getClickEventData(e)
    });
  }

  protected isOtherVisualCrossFilterSource() {
    if (this.interactions?.crossFilter)
      return this.interactions?.crossFilter.source() != this.host;

    return false;
  }

  protected getClickEventData(e: MouseEvent): VisualHost.ClickEventData {
    return {
      action: VisualHost.ClickAction.None,
      resetCrossFilter: true,
    };
  }


  constructor() {
    this.broadcastEvent({ type: VisualHost.EventType.RenderStart });
  }
}

export namespace VisualComponent {
  export class Formatter {
    constructor(
      public readonly component: VisualComponent,
      public readonly dataFormatter: DataFormatter,
      public readonly visual: Visual
    ) { }

    /** Gets display string for a value of the given field. */
    getValueDisplay(field: VisualField, value: any, args?: DisplayFormatArgs): string {
      let preparedValue = this.component.beforeFormatValueForDisplay(value);

      let formatted = this.dataFormatter.formatForDisplay(preparedValue, {
        dataType: field.dataType,
        isAggregated: field.isAggregated,
        aggregation: field.aggregation,
        display: field.dataDisplay,
        ...args
      });

      return this.component.afterFormatValueForDisplay(formatted);
    }

    /** Formats value of the given field. */
    getValueData(field: VisualField, value: any): any {
      let preparedValue = this.component.beforeFormatValueForData(value);

      let formatted = this.dataFormatter.applyDataFormat(preparedValue, {
        dataType: field.dataType,
        isAggregated: field.isAggregated,
        aggregation: field.aggregation,
        format: field.dataFormat
      });

      return this.component.afterFormatValueForData(formatted);
    }
  }

  export class RowFormatter extends Formatter {
    constructor(
      public readonly component: VisualComponent,
      public readonly dataFormatter: DataFormatter,
      public readonly visual: Visual,
      public readonly columns: VisualDataQueryColumnCollection
    ) { super(component, dataFormatter, visual); }

    private getColumn(field: VisualField) {
      return this.columns.get(field);
    }

    /** Gets display string for a value of the given field from a row. */
    getRowValueDisplay(row: ReadonlyArray<any>, field: VisualField, args?: DisplayFormatArgs): string {
      let column = this.getColumn(field);
      return this.getValueDisplay(field, row[column.dataIndex], args);
    }

    /** Formats value of the given field from a row. */
    getRowValueData(row: ReadonlyArray<any>, field: VisualField): any {
      let column = this.getColumn(field);
      return this.getValueData(field, row[column.dataIndex]);
    }
  }

  export interface Data {
    /** 
     * Index of this request. 
     * This can be used to identify when a request changes.
     * This will not change if the request instance changes, but the value is the same. 
     **/
    requestIndex(): number;

    /**
     * Whether the data request is loading. 
     * This should indicate the initial query loading, not data load from query results. 
     **/
    loading(): boolean;

    /**
     * Whether the data request is loaded (successfully or not). 
     **/
    loaded(): boolean;

    /**
     * Whether the data request has been loaded successfully. 
     * `false` when not `loaded()` or `failed()`.
     **/
    success(): boolean;

    /**
     * Whether the data request has failed to load. 
     * `false` when not `loaded()` or `success()`.
     **/
    failure(): boolean;

    /**
     * Details about the data request load failure.
     * `null` when not `loaded()` or `failed()`.
     **/
    error(): ErrorDetails;
    

    /**
     * Whether any data is being fetched. 
     * `false` when not `loaded()` and not `success()`, or when no data is being fetched.
     **/
    fetching() : boolean;
    
    /**
     * Same as `loading() && fetching()`.
     **/
    loadingOrFetching(): boolean;


    /**
     * Gets state of row count for this visual.
     * If the visual does not support row count, returns `undefined`.
     **/
    rowCount(): VisualRowCountState | undefined;

    /**
     * Gets a the VisualContnetError state detailing if the data load has failed or returned not enough or to little data.
     * Returns undefined if the query is has not completed or is successful.
     **/
    contentError(): VisualContentError | undefined;

    onDestroy(): void;
  }

  let nextRequestIndex = 1;

  export class DefaultData implements Data {
    constructor(
      public readonly requestGetter: Signal<VisualDataRequest>,
      public readonly dataSource: Signal<VisualDataSource>) { }

    readonly request = linkedSignal<VisualDataRequest>(() => {
      let request = this.requestGetter();
      if (!request)
        return null;

      return request;
    }, {
      equal: (a, b) => a === b || a?.cacheKey == b?.cacheKey
    });

    readonly requestIndex = computed(() => {
      let request = this.request();
      return nextRequestIndex++;
    });

    onDestroy() {
      this.abortQuery();
    }

    abortQuery() {
      untracked(() => {
        let query = this.query();
        if (query) {
          query.abort();
          this.query.set(null);
        }
      });
    }

    readonly query = signal<VisualDataQuery>(null);

    private readonly __queryChanged = effect(() => {
      untracked(() => this.abortQuery());

      let request = this.request();
      if (!request?.fields?.length || !request.isValid) {
        this.query.set(null);
        return;
      }

      let newQuery = this.dataSource().query(request);
      this.query.set(newQuery);
    });
    

    readonly loading = computed(() => !!this.query()?.executing());
    readonly loaded  = computed(() => this.query() && !!this.query().completed());
    readonly success = computed(() => this.query() && !!this.query().success());
    readonly failure = computed(() => this.query() && !!this.query().failure());
    readonly error   = computed(() => this.failure() ? this.query().error() : null);

    readonly fetching = computed(() => !!this.query()?.resources.anyLoading());
    readonly loadingOrFetching = computed(() => this.loading() || this.fetching());

    
    readonly table = computed(() => {
      let query = this.query();
      if (!query)
        return null;

      if (!query.success())
        return null;

      return query.data()?.adapter;
    });


    readonly columns = computed(() => this.table()?.columns);

    readonly totalRows = computed(() => this.table()?.totalRows);
    readonly exceededRowLimits = computed(() => this.table()?.exceededRowLimits);

    readonly rowsPerChunk = computed(() => this.table()?.rowsPerChunk);
    readonly chunks = computed(() => this.table()?.chunks);
    
    readonly rowCount = computed<VisualRowCountState>(() => {
      let query = this.query();
      if (!query || !query.completed())
        return { loaded: false };

      if (query.failure()) {
        return {
          loaded: true,
          error: query.error
        };
      }
      else {
        let table = this.table();
        if (!table) {
          return {
            loaded: false,
            rowCount: 0
          };
        }

        return {
          loaded: true,
          rowCount: table.totalRows,
          exceededRowLimit: this.table().exceededRowLimits
        };
      }
    });

    readonly contentError = computed<VisualContentError>(() => {
      let query = this.query();
      if (!query || !query.completed()) {
        let request = this.request();
        if (!request?.isValid) {
          return VisualContentError.Generic;
        }

        return null;
      }

      if (this.query().success()) {
        let rowCount = this.rowCount();
        if (rowCount?.loaded) {
          if (rowCount.rowCount < 1)
            return VisualContentError.NoData;

          if (rowCount.exceededRowLimit)
            return VisualContentError.ToMuchData;
        }
        else {
          let table = this.table();
          if (table) {
            if (table?.totalRows < 1)
              return VisualContentError.NoData;

            if (table?.exceededRowLimits)
              return VisualContentError.ToMuchData;
          }
        }

        return null;
      }

      return VisualContentError.Generic;
    });


    chunk(r: VisualDataAdapter.ChunkRequest): VisualDataResource<ChunkResult> | null {
      return this.table()?.chunk(r);
    }

    rows(r?: VisualDataAdapter.RowsRequest): VisualDataResource<RowsResult> | null {
      return this.table()?.rows({ ...r });
    }

    distinctValues(r: VisualDataAdapter.DistinctValuesRequest): VisualDataResource<RowsResult> | null {
      return this.table()?.distinctValues(r);
    }

    distinctRows(r: VisualDataAdapter.DistinctRowsRequest): VisualDataResource<RowsResult> | null {
      return this.table()?.distinctRows(r);
    }

    aggregateColumn(r: VisualDataAdapter.AggregateColumnRequest): VisualDataResource<RowsResult> | null {
      return this.table()?.aggregateColumn(r);
    }

    aggregateColumns(r: VisualDataAdapter.AggregateColumnsRequest): VisualDataResource<RowsResult> | null {
      return this.table()?.aggregateColumns(r);
    }

    matchingValues(r: VisualDataAdapter.MatchingValuesRequest): VisualDataResource<RowsResult> | null {
      return this.table()?.matchingValues(r);
    }
  }
}
